import { defineStore } from 'pinia';
import { DelayTheAlarmCommand } from 'src/stores/alarm/commands/DelayTheAlarmCommand';
import { DelayTheAlarmToASpecificTimestampCommand } from 'src/stores/alarm/commands/DelayTheAlarmToASpecificTimestampCommand';
import {
	GetAllUnconfirmedAlarmsForUserQuery,
	GetAllUnconfirmedBackgroundRefreshAlarmsForUserQuery,
} from 'src/stores/alarm/queries';
import createAlarmState, { AlarmStateInterface } from 'src/stores/alarm/state';

import { AlarmRunType, AlarmWithUserAlarm, AlarmWithUserAlarmRunType } from 'src/models/alarm';

import { AlarmActionOutcome, AlarmActionResult } from './alarmActionOutcome';
import { BackgroundAlarmCallback, ForegroundAlarmCallback } from './callbacks';
import { NativeAlarmIdCollisionError, NativeAlarmIdConflictError } from './callbacks/AlarmNotificationReconciler';
import {
	BackgroundFetchAlarmsPayload,
	DelayAlarmPayload,
	DelayAlarmToSpecificTimestampPayload,
	FetchAlarmsPayload,
} from './interfaces';

const createDefaultState = (): AlarmStateInterface => createAlarmState();

export const normalizeAlarmAggregateId = (alarmId: number | string): number | undefined => {
	if (typeof alarmId === 'string' && !/^\d+$/.test(alarmId)) {
		return undefined;
	}

	const aggregateId = typeof alarmId === 'number' ? alarmId : Number(alarmId);
	return Number.isSafeInteger(aggregateId) && aggregateId > 0 ? aggregateId : undefined;
};

export const useAlarmStore = defineStore('alarm', {
	actions: {
		async backgroundFetchAlarms(payload: BackgroundFetchAlarmsPayload): Promise<AlarmActionResult> {
			if (payload.queryStore === undefined) {
				return 'failure';
			}

			try {
				const callback = new BackgroundAlarmCallback(
					payload.localNotifications,
					(alarms) => {
						this.setAlarms(alarms);
					},
					payload.t
				);
				const acquisitionCompletion = callback.waitForAcquisitionCompletion();
				await payload.queryStore.query<Array<AlarmWithUserAlarm>>({
					callback,
					createTime: new Date(),
					query: new GetAllUnconfirmedBackgroundRefreshAlarmsForUserQuery(
						payload.browser,
						process.env.VERSION,
						payload.platform
					),
					responseType: 'json',
					runType: AlarmRunType,
				});
				if (!callback.hasAcquisitionStarted()) {
					return 'failure';
				}

				const acquisition = await acquisitionCompletion;
				if (acquisition.state === 'failure') {
					return 'failure';
				}
				return 'success';
			} catch (error) {
				if (error instanceof NativeAlarmIdCollisionError) {
					return { outcome: 'failure', reason: error.reason };
				}
				if (error instanceof NativeAlarmIdConflictError) {
					return { outcome: 'failure', reason: error.reason };
				}

				return 'failure';
			}
		},
		async delayAlarm(payload: DelayAlarmPayload): Promise<void> {
			if (payload.commandBus === undefined) {
				return;
			}

			const response = await payload.commandBus.sendCommand<{ success?: boolean }>(
				new DelayTheAlarmCommand(payload.type, payload.id, payload.delayDuration)
			);
			if (response === null || response?.data?.success === false) {
				throw new Error('DelayTheAlarmCommand was not accepted by the backend.');
			}
		},
		async delayAlarmToASpecificTimestamp(payload: DelayAlarmToSpecificTimestampPayload): Promise<void> {
			if (payload.commandBus === undefined) {
				return;
			}

			const response = await payload.commandBus.sendCommand<{ success?: boolean }>(
				new DelayTheAlarmToASpecificTimestampCommand(payload.type, payload.id, payload.timestamp)
			);
			if (response === null || response?.data?.success === false) {
				throw new Error('DelayTheAlarmToASpecificTimestampCommand was not accepted by the backend.');
			}
		},
		fetchAlarms(payload: FetchAlarmsPayload): AlarmActionOutcome | void {
			if (payload.queryStore !== undefined) {
				void payload.queryStore.query<Array<AlarmWithUserAlarm>>({
					callback: new ForegroundAlarmCallback(
						payload.localNotifications,
						(alarms) => {
							this.setAlarms(alarms);
						},
						payload.t
					),
					createTime: new Date(),
					query: new GetAllUnconfirmedAlarmsForUserQuery(),
					responseType: 'json',
					runType: AlarmWithUserAlarmRunType,
				});
				return;
			}

			return 'failure';
		},
		removeAlarm(payload: { id: string; type: string }): void {
			this.alarms = this.alarms.filter((alarm) => !(alarm.id === payload.id && alarm.type === payload.type));
		},
		reset(): void {
			this.alarms = [];
		},
		setAlarms(payload: AlarmWithUserAlarm[] | null): void {
			this.alarms = payload ?? [];
		},
	},
	state: createDefaultState,
});
export * from './interfaces';
export * from './types';
